import { useState, useEffect, useRef } from "react";
import LandingPagePanel from "../components/admin/LandingPagePanel";
import ConfirmModal from "../components/admin/ConfirmModal";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { 
    name: "Landing Page", 
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>,
    subsections: [
      { key: "hero", label: "Hero Section" },
      { key: "about", label: "About Us" },
      { key: "programs", label: "Programs" },
      { key: "stats", label: "Statistics" },
      { key: "why_us", label: "Why Choose Us" },
      { key: "intakes", label: "Intake Windows" },
      { key: "expo", label: "Expo Section" },
      { key: "testimonials", label: "Testimonials" },
      { key: "admissions", label: "Admissions" },
      { key: "gallery", label: "Gallery Section" },
    ]
  },
  { name: "Gallery", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> },
  { name: "Applications", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg> },
  { name: "Opportunities", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg> },
];

const STATUS_COLORS = {
  pending: "#f59e0b", reviewing: "#3b82f6",
  accepted: "#22c55e", rejected: "#ef4444", waitlisted: "#8b5cf6",
};
const STATUSES = ["pending", "reviewing", "accepted", "rejected", "waitlisted"];

export default function AdminDashboard() {
  const { user, logout, authFetch } = useAuth();
  const [tab, setTab] = useState("Landing Page");
  const [activeSection, setActiveSection] = useState("hero");
  const [isLandingExpanded, setIsLandingExpanded] = useState(true);
  const [confirmState, setConfirmState] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Close mobile sidebar when changing tabs/sections
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [tab, activeSection]);

  const handleLogoutClick = () => {
    setConfirmState({
      title: "Confirm Logout",
      message: "Are you sure you want to log out of the admin dashboard?",
      confirmText: "Logout",
      confirmVariant: "danger",
      onConfirm: logout,
    });
  };

  const handleTabClick = (tabName) => {
    if (tabName === "Landing Page") {
      setIsLandingExpanded(!isLandingExpanded);
      if (tab !== "Landing Page") setTab("Landing Page");
    } else {
      setTab(tabName);
      setIsLandingExpanded(false);
    }
  };

  const getPageTitle = () => {
    if (tab === "Landing Page") {
      const section = TABS[0].subsections.find(s => s.key === activeSection);
      return `Landing Page - ${section?.label || ""}`;
    }
    return tab;
  };

  return (
    <div className="admin-layout">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div className="admin-sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}

      <aside className={`admin-sidebar ${isSidebarOpen ? "admin-sidebar--open" : ""}`}>
        <div className="admin-sidebar__logo">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img src="/logo.jpeg" alt="logo" width={32} height={32} style={{ borderRadius: 6 }} />
            <span>Web Admin</span>
          </div>
          <button className="admin-sidebar-close" onClick={() => setIsSidebarOpen(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <nav className="admin-sidebar__nav">
          {TABS.map(t => (
            <div key={t.name} className="admin-sidebar__group">
              <button
                className={`admin-sidebar__item ${tab === t.name ? "admin-sidebar__item--active" : ""}`}
                onClick={() => handleTabClick(t.name)}>
                <div className="admin-sidebar__item-main">
                  {t.icon}
                  <span>{t.name}</span>
                </div>
                {t.subsections && (
                  <svg className={`admin-sidebar__chevron ${isLandingExpanded ? "admin-sidebar__chevron--expanded" : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                )}
              </button>
              
              {t.subsections && (
                <div className={`admin-sidebar__submenu ${isLandingExpanded ? "admin-sidebar__submenu--open" : ""}`}>
                  {t.subsections.map(sub => (
                    <button
                      key={sub.key}
                      className={`admin-sidebar__submenu-item ${tab === "Landing Page" && activeSection === sub.key ? "admin-sidebar__submenu-item--active" : ""}`}
                      onClick={() => {
                        setTab("Landing Page");
                        setActiveSection(sub.key);
                      }}>
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <div className="admin-sidebar__user-info">
            <div className="admin-sidebar__user-avatar">
              {user?.name?.charAt(0).toUpperCase() || "A"}
            </div>
            <div className="admin-sidebar__user-details">
              <p className="admin-sidebar__user-name">{user?.name}</p>
              <p className="admin-sidebar__user-role">Administrator</p>
            </div>
          </div>
        </div>
      </aside>

      <div className="admin-body">
        <header className="admin-topbar">
          <div className="admin-topbar__left">
            <button className="admin-hamburger" onClick={() => setIsSidebarOpen(true)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
            <h2 className="admin-topbar__title">{getPageTitle()}</h2>
          </div>
          <div className="admin-topbar__right">
            <button className="admin-logout-btn" onClick={handleLogoutClick}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        </header>

        <main className="admin-main">
          {tab === "Landing Page" && (
            <LandingPagePanel 
              authFetch={authFetch} 
              activeSection={activeSection}
              onOpenGallery={() => {
                setTab("Gallery");
                setIsLandingExpanded(false);
              }} 
            />
          )}
          {tab === "Gallery"       && <GalleryPanel authFetch={authFetch} onConfirmRequest={setConfirmState} />}
          {tab === "Applications"  && <ApplicationsPanel authFetch={authFetch} onConfirmRequest={setConfirmState} />}
          {tab === "Opportunities" && <OpportunitiesPanel authFetch={authFetch} onConfirmRequest={setConfirmState} />}
        </main>
      </div>

      <ConfirmModal state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

// ── Gallery Panel ────────────────────────────────────────────
function GalleryPanel({ authFetch, onConfirmRequest }) {
  const [items, setItems]     = useState([]);
  const [form, setForm]       = useState({ title: "", description: "" });
  const [editId, setEditId]   = useState(null);
  const [files, setFiles]     = useState([]);
  const [previews, setPreviews] = useState([]);
  const [msg, setMsg]         = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  const notify = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3500);
  };

  const load = async () => {
    const res  = await authFetch("/gallery");
    const data = await res.json();
    setItems(Array.isArray(data) ? data : []);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleFile = e => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    for (const f of selected) {
      if (f.size > 10 * 1024 * 1024) { notify(`"${f.name}" must be under 10 MB`, "error"); return; }
      if (!allowed.includes(f.type))  { notify(`"${f.name}" must be JPG, PNG or WebP`, "error"); return; }
    }
    setFiles(selected);
    setPreviews(selected.map(f => URL.createObjectURL(f)));
  };

  const reset = () => {
    setForm({ title: "", description: "" });
    setFiles([]); setPreviews([]); setEditId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title.trim()) { notify("Event title is required", "error"); return; }
    if (!editId && !files.length) { notify("Please select at least one photo", "error"); return; }
    setLoading(true);
    try {
      if (editId) {
        const res  = await authFetch(`/gallery/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: form.title.trim(), description: form.description.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Update failed");
        notify("Updated successfully");
      } else {
        const fd = new FormData();
        fd.append("title", form.title.trim());
        fd.append("description", form.description.trim());
        files.forEach(f => fd.append("images[]", f));
        const res  = await authFetch("/gallery", { method: "POST", body: fd });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Upload failed");
        notify(data.message || "Uploaded successfully");
      }
      reset(); load();
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    onConfirmRequest({
      title: "Delete Photo",
      message: "Are you sure you want to delete this photo? This action cannot be undone.",
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: async () => {
        const res  = await authFetch(`/gallery/${id}`, { method: "DELETE" });
        const data = await res.json();
        if (res.ok) { notify("Deleted"); load(); }
        else notify(data.error || "Delete failed", "error");
      }
    });
  };

  // Group items into events by title + description
  const events = {};
  items.forEach(item => {
    const key = `${item.title}||${item.description || ""}`;
    if (!events[key]) events[key] = { title: item.title, description: item.description || "", images: [] };
    events[key].images.push(item);
  });
  const eventList = Object.values(events);

  return (
    <>
      <header className="admin-header">
        <h1>Gallery Management</h1>
        {msg.text && <div className={`admin-toast admin-toast--${msg.type}`}>{msg.text}</div>}
      </header>

      {/* Upload form */}
      <section className="admin-card">
        <h2>{editId ? "Edit Photo Info" : "Upload Event Photos"}</h2>
        <form onSubmit={handleSubmit} className="admin-form">
          <label className="admin-form__label">Event Title *
            <input className="admin-form__input" required value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Graduation Ceremony 2025" />
          </label>
          <label className="admin-form__label">Event Description
            <textarea className="admin-form__input admin-form__textarea" rows={3}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of the event…" />
          </label>
          {!editId && (
            <label className="admin-form__label">
              Photos (JPG, PNG, WebP — max 10 MB each) *
              <input ref={fileRef} type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple className="admin-form__input" onChange={handleFile} />
              {files.length > 0 && (
                <span style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 4, display: "block" }}>
                  {files.length} photo{files.length !== 1 ? "s" : ""} selected
                </span>
              )}
            </label>
          )}
          {previews.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {previews.map((src, i) => (
                <img key={i} src={src} alt={`preview-${i}`} className="admin-form__preview" />
              ))}
            </div>
          )}
          <div className="admin-form__actions">
            <button type="submit" className="btn btn--accent" disabled={loading}>
              {loading ? "Saving…" : editId ? "Update" : "Upload Photos"}
            </button>
            {editId && <button type="button" className="btn btn--outline" onClick={reset}>Cancel</button>}
          </div>
        </form>
      </section>

      {/* Events list */}
      <section className="admin-card">
        <h2>All Events ({eventList.length})</h2>
        {eventList.length === 0 ? (
          <p style={{ color: "var(--text-muted)" }}>No photos yet.</p>
        ) : (
          eventList.map((event, ei) => (
            <div key={ei} style={{ marginBottom: 32, borderBottom: "1px solid rgba(0,0,0,0.07)", paddingBottom: 24 }}>
              <div style={{ marginBottom: 12 }}>
                <strong style={{ fontSize: "1rem", color: "var(--primary)" }}>{event.title}</strong>
                {event.description && (
                  <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: 2 }}>{event.description}</p>
                )}
                <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  {event.images.length} photo{event.images.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="admin-gallery-grid">
                {event.images.map(item => (
                  <div key={item.id} className="admin-gallery-item">
                    <img src={item.image_url} alt={item.title} className="admin-gallery-item__img" />
                    <div className="admin-gallery-item__actions">
                      <button className="btn btn--outline" onClick={() => {
                        setEditId(item.id);
                        setForm({ title: item.title, description: item.description || "" });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}>Edit</button>
                      <button className="btn btn--accent"
                        style={{ background: "#c62828", borderColor: "#c62828" }}
                        onClick={() => handleDelete(item.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </section>
    </>
  );
}

// ── Applications Panel ───────────────────────────────────────
function ApplicationsPanel({ authFetch }) {
  const [apps, setApps]         = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [filter, setFilter]     = useState("all");
  const [statusForm, setStatusForm] = useState({ status: "", notes: "" });
  const [msg, setMsg]           = useState({ text: "", type: "" });
  const [loading, setLoading]   = useState(false);
  const [detailError, setDetailError] = useState("");

  const notify = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3500);
  };

  const load = async () => {
    const url  = filter === "all" ? "/applications" : `/applications?status=${filter}`;
    const res  = await authFetch(url);
    const data = await res.json();
    setApps(Array.isArray(data) ? data : []);
  };

  const loadDetail = async id => {
    try {
      const res  = await authFetch(`/applications/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load");
      setDetail(data); setDetailError("");
      setStatusForm({ status: data.status_name, notes: data.admin_notes || "" });
    } catch (err) {
      setDetail(null); setDetailError(err.message);
      notify(err.message, "error");
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const handleStatusUpdate = async () => {
    setLoading(true);
    try {
      const res  = await authFetch(`/applications/${selected}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(statusForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Update failed");
      notify("Status updated");
      load(); loadDetail(selected);
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="admin-header">
        <h1>Applications</h1>
        {msg.text && <div className={`admin-toast admin-toast--${msg.type}`}>{msg.text}</div>}
      </header>

      <div className="admin-card" style={{ padding: "16px 20px", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["all", ...STATUSES].map(s => (
            <button key={s}
              className={`btn ${filter === s ? "btn--accent" : "btn--outline"}`}
              style={{ padding: "6px 14px", fontSize: "0.82rem" }}
              onClick={() => { setFilter(s); setSelected(null); setDetail(null); setDetailError(""); }}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="apps-layout">
        <div className="admin-card apps-list">
          <h2>Applications ({apps.length})</h2>
          {apps.length === 0
            ? <p style={{ color: "var(--text-muted)" }}>No applications found.</p>
            : apps.map(app => (
              <div key={app.id}
                className={`app-row ${selected === app.id ? "app-row--active" : ""}`}
                onClick={() => { setSelected(app.id); loadDetail(app.id); }}>
                <div className="app-row__name">{app.full_name}</div>
                <div className="app-row__meta">{app.program} · {app.intake_year}</div>
                <span className="app-row__status"
                  style={{ background: STATUS_COLORS[app.status] + "22", color: STATUS_COLORS[app.status] }}>
                  {app.status}
                </span>
              </div>
            ))}
        </div>

        {detail && (
          <div className="admin-card apps-detail">
            <h2>{detail.first_name} {detail.last_name}</h2>
            <div className="apps-detail__grid">
              <DetailRow label="Email"         value={detail.email} />
              <DetailRow label="Phone"         value={detail.phone} />
              <DetailRow label="Program"       value={detail.program_name} />
              <DetailRow label="Intake"        value={detail.intake_year} />
              {detail.edu_level !== undefined && (
                <DetailRow label="Edu Level"     value={detail.edu_level === "grade11" ? "Grade 11+" : "Standard 7"} />
              )}
              <DetailRow label="ID No."        value={detail.id_number} />
              <DetailRow label="DOB"           value={detail.date_of_birth} />
              <DetailRow label="School"        value={detail.school_name} />
              <DetailRow label="Qualification" value={detail.highest_qual} />
              <DetailRow label="Address"
                value={[detail.address_street, detail.address_city, detail.address_district, detail.address_country].filter(Boolean).join(", ")}
                full />
            </div>

            <div className="apps-detail__motivation">
              <strong>Motivation</strong>
              <p>{detail.motivation || "—"}</p>
            </div>

            <div className="apps-detail__status-form">
              <h4>Update Status</h4>
              <select className="admin-form__input" value={statusForm.status}
                onChange={e => setStatusForm({ ...statusForm, status: e.target.value })}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <textarea className="admin-form__input admin-form__textarea" rows={3}
                placeholder="Admin notes (optional)…"
                value={statusForm.notes}
                onChange={e => setStatusForm({ ...statusForm, notes: e.target.value })} />
              <button className="btn btn--accent" onClick={handleStatusUpdate} disabled={loading}>
                {loading ? "Saving…" : "Update Status"}
              </button>
            </div>

            <div className="apps-detail__docs">
              <h4>Submitted Documents</h4>
              {detail.documents?.length > 0 ? detail.documents.map((doc, i) => (
                <div key={i} className="apps-detail__doc-row">
                  <span className="apps-detail__doc-icon">📄</span>
                  <span className="apps-detail__doc-label">{doc.doc_label || doc.doc_type}</span>
                  <a href={doc.file_url} target="_blank" rel="noreferrer" className="apps-detail__doc-link">View ↗</a>
                </div>
              )) : <p style={{ color: "var(--text-muted)" }}>No documents uploaded yet.</p>}
            </div>

            {detail.history?.length > 0 && (
              <div className="apps-detail__history">
                <h4>Status History</h4>
                {detail.history.map((h, i) => (
                  <div key={i} className="history-row">
                    <span className="history-row__status" style={{ color: STATUS_COLORS[h.status] }}>{h.status}</span>
                    <span className="history-row__meta">{h.changed_by_name || "System"} · {new Date(h.changed_at).toLocaleDateString()}</span>
                    {h.notes && <p className="history-row__notes">{h.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!detail && selected && detailError && (
          <div className="admin-card apps-detail">
            <p style={{ color: "#c62828" }}>{detailError}</p>
          </div>
        )}
      </div>
    </>
  );
}

function DetailRow({ label, value, full }) {
  return (
    <div className={`apps-detail__row ${full ? "apps-detail__row--full" : ""}`}>
      <span className="apps-detail__label">{label}</span>
      <span className="apps-detail__value">{value || "—"}</span>
    </div>
  );
}

// ── Opportunities Panel ────────────────────────────────────
const OPP_TYPES = ['job','internship','scholarship','event','announcement','other'];
const TYPE_COLORS = {
  job: '#3b82f6', internship: '#8b5cf6', scholarship: '#22c55e',
  event: '#f59e0b', announcement: '#1E63C6', other: '#6b7280',
};

const EMPTY_OPP = { title: '', type: 'announcement', description: '', deadline: '', is_active: 1 };

function OpportunitiesPanel({ authFetch, onConfirmRequest }) {
  const [items, setItems]   = useState([]);
  const [form, setForm]     = useState(EMPTY_OPP);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg]       = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);

  const notify = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3500);
  };

  const load = async () => {
    const res  = await authFetch('/opportunities/all');
    const data = await res.json();
    setItems(Array.isArray(data) ? data : []);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = editId
        ? await authFetch(`/opportunities/${editId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(form),
          })
        : await authFetch('/opportunities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(form),
          });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      notify(editId ? 'Updated' : 'Created');
      setForm(EMPTY_OPP);
      setEditId(null);
      load();
    } catch (err) { notify(err.message, 'error'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    onConfirmRequest({
      title: "Delete Opportunity",
      message: "Are you sure you want to delete this opportunity? This action cannot be undone.",
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: async () => {
        const res  = await authFetch(`/opportunities/${id}`, { method: 'DELETE' });
        const data = await res.json();
        if (res.ok) { notify('Deleted'); load(); } else { notify(data.error, 'error'); }
      }
    });
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      title: item.title, type: item.type,
      description: item.description,
      deadline: item.deadline ? item.deadline.substring(0, 10) : '',
      is_active: item.is_active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <>
      <header className="admin-header">
        <h1>Opportunities</h1>
        {msg.text && <div className={`admin-toast admin-toast--${msg.type}`}>{msg.text}</div>}
      </header>

      <section className="admin-card">
        <h2>{editId ? 'Edit Opportunity' : 'Create New Opportunity'}</h2>
        <form onSubmit={handleSubmit} className="admin-form" style={{ maxWidth: 680 }}>
          <label className="admin-form__label">Title *
            <input className="admin-form__input" value={form.title} required
              onChange={e => set('title', e.target.value)} placeholder="e.g. Graphic Design Internship" />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <label className="admin-form__label">Type *
              <select className="admin-form__input" value={form.type} onChange={e => set('type', e.target.value)}>
                {OPP_TYPES.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </label>
            <label className="admin-form__label">Deadline (optional)
              <input type="date" className="admin-form__input" value={form.deadline}
                onChange={e => set('deadline', e.target.value)} />
            </label>
          </div>

          <label className="admin-form__label">Description *
            <textarea className="admin-form__input admin-form__textarea" rows={5} required
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Describe the opportunity, requirements, how to apply..." />
          </label>

          <label className="admin-form__label" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" checked={form.is_active === 1}
              onChange={e => set('is_active', e.target.checked ? 1 : 0)} />
            Visible to public
          </label>

          <div className="admin-form__actions">
            <button type="submit" className="btn btn--accent" disabled={loading}>
              {loading ? 'Saving…' : editId ? 'Update' : 'Create'}
            </button>
            {editId && (
              <button type="button" className="btn btn--outline"
                onClick={() => { setEditId(null); setForm(EMPTY_OPP); }}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="admin-card">
        <h2>All Opportunities ({items.length})</h2>
        {items.length === 0
          ? <p style={{ color: 'var(--text-muted)' }}>No opportunities yet.</p>
          : (
            <div className="opp-list">
              {items.map(item => (
                <div key={item.id} className={`opp-item ${!item.is_active ? 'opp-item--inactive' : ''}`}>
                  <div className="opp-item__left">
                    <span className="opp-item__type" style={{ background: (TYPE_COLORS[item.type] || '#6b7280') + '22', color: TYPE_COLORS[item.type] || '#6b7280' }}>
                      {item.type}
                    </span>
                    <div>
                      <p className="opp-item__title">{item.title}</p>
                      <p className="opp-item__meta">
                        {item.deadline ? `Deadline: ${new Date(item.deadline).toLocaleDateString()}` : 'No deadline'}
                        {' · '}{item.is_active ? 'Visible' : 'Hidden'}
                      </p>
                    </div>
                  </div>
                  <div className="opp-item__actions">
                    <button className="btn btn--outline" onClick={() => handleEdit(item)}>Edit</button>
                    <button className="btn btn--accent"
                      style={{ background: '#c62828', borderColor: '#c62828' }}
                      onClick={() => handleDelete(item.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </section>
    </>
  );
}
